import React, { useState, useEffect, useContext } from "react";
import UploadDocumentsModal from "./UploadDocumentsModal";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { supabase } from "@/lib/supabaseClient";
import { ProfileContext } from "@/context/ProfileContext";
import { useTranslation } from "react-i18next";

/* ===========================
   Helpers fiscaux & utilitaires
   =========================== */

// Régimes supportés (profil utilisateur)
const REGIMES = {
  NONE: "NONE",
  FR_FORFAIT_6_5: "FR_FORFAIT_6_5", // 6% + 0.5% CRDS sur le PRIX (> 5 000€ par cession)
  FR_PLUS_VALUE: "FR_PLUS_VALUE", // 19% + 17.2% PS sur la PLUS-VALUE, abattement 5%/an après 2 ans, exonérée à 22 ans
  UK_CGT_SIMPLE_20: "UK_CGT_SIMPLE_20",
  DE_PRIVATE_1Y_RULE: "DE_PRIVATE_1Y_RULE",
};

// parse date "YYYY-MM-DD" ou "DD/MM/YYYY"
const parseDateSmart = (s) => {
  if (!s) return null;
  // ISO
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const d = new Date(s);
    return isNaN(d) ? null : d;
  }
  // FR
  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) {
    const [, dd, mm, yyyy] = m;
    const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return isNaN(d) ? null : d;
  }
  const d = new Date(s);
  return isNaN(d) ? null : d;
};

const yearsBetween = (d1, d2) => {
  try {
    const a = parseDateSmart(d1);
    const b = parseDateSmart(d2);
    if (!a || !b) return 0;
    return Math.floor((b - a) / (365.25 * 24 * 3600 * 1000));
  } catch {
    return 0;
  }
};

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

// FR – Taxe forfaitaire 6.5% (seuil 5 000€ par cession)
const frTaxeForfaitaire = (salePrice) => {
  if (!salePrice || salePrice <= 5000) return 0;
  return salePrice * 0.065;
};

// FR – Plus-value (19% + 17.2%) avec abattement 5%/an après 2 ans, exonération à 22 ans
const frPlusValueTax = ({ salePrice, purchasePrice, acquisitionDate, saleDate }) => {
  const sp = Number(salePrice || 0);
  const pp = Number(purchasePrice || 0);
  if (!sp || !pp || pp <= 0) return null; // données insuffisantes
  const gain = sp - pp;
  if (gain <= 0) return 0;

  const heldYears = yearsBetween(acquisitionDate, saleDate);
  const abattementYears = Math.max(0, heldYears - 2);
  const abattementRate = clamp(abattementYears * 0.05, 0, 1);
  const taxable = gain * (1 - abattementRate);

  if (taxable <= 0 || heldYears >= 22) return 0;

  const ir = taxable * 0.19;
  const ps = taxable * 0.172;
  return ir + ps;
};

// Impôt pour UNE vente (France)
const computeSaleTaxFR = ({ regime, sale }) => {
  const salePrice = Number(sale?.amount || sale?.price || 0) || 0;

  if (regime === REGIMES.FR_FORFAIT_6_5) {
    return frTaxeForfaitaire(salePrice);
  }

  if (regime === REGIMES.FR_PLUS_VALUE) {
    return frPlusValueTax({
      salePrice,
      purchasePrice: Number(sale?.purchase_price || sale?.acquisition_price || 0) || 0,
      acquisitionDate: sale?.acquisition_date || sale?.purchase_date,
      saleDate: sale?.date,
    });
  }

  return null;
};

// --- 🇬🇧 UK – Estimation simplifiée CGT 20% sur la plus-value
const ukCgtSimple = ({ salePrice, purchasePrice }) => {
  const sp = Number(salePrice || 0);
  const pp = Number(purchasePrice || 0);
  if (!sp || !pp || pp <= 0) return null;
  const gain = sp - pp;
  if (gain <= 0) return 0;
  return gain * 0.2; // estimation haute simplifiée
};

// --- 🇩🇪 DE – Exonéré si détention > 1 an, sinon non estimé (dépend IR)
const dePrivateRule = ({ purchaseDate, saleDate }) => {
  const held = yearsBetween(purchaseDate, saleDate);
  if (held > 1) return 0;
  return null; // non estimable (<= 1 an)
};

// --- Calcul annuel international (UK / DE)
const computeYearTaxINTL = ({ regime, salesInYear }) => {
  if (regime === REGIMES.NONE) return null;
  const ventes = (salesInYear || []).filter(
    (t) => (t?.type || "").toLowerCase() === "vente"
  );

  if (regime === REGIMES.UK_CGT_SIMPLE_20) {
    let total = 0;
    for (const v of ventes) {
      const t = ukCgtSimple({
        salePrice: v?.amount,
        purchasePrice: v?.acquisition_price,
      });
      if (t === null) return null;
      total += t;
    }
    return total;
  }

  if (regime === REGIMES.DE_PRIVATE_1Y_RULE) {
    let total = 0;
    for (const v of ventes) {
      const t = dePrivateRule({
        purchaseDate: v?.acquisition_date || v?.purchase_date,
        saleDate: v?.date,
      });
      if (t === null) return null; // non estimable si détention ≤ 1 an
      total += t; // 0 si exonéré
    }
    return total;
  }

  return null;
};

// --- Calcul annuel France
const computeYearTaxFR = ({ regime, salesInYear }) => {
  if (regime === REGIMES.NONE) return null;
  const ventes = (salesInYear || []).filter(
    (t) => (t?.type || "").toLowerCase() === "vente"
  );

  if (regime === REGIMES.FR_FORFAIT_6_5) {
    return ventes.reduce(
      (sum, v) => sum + (computeSaleTaxFR({ regime, sale: v }) || 0),
      0
    );
  }

  if (regime === REGIMES.FR_PLUS_VALUE) {
    let total = 0;
    for (const v of ventes) {
      const t = computeSaleTaxFR({ regime, sale: v });
      if (t === null) return null; // données insuffisantes -> on remonte null
      total += t;
    }
    return total;
  }

  return null;
};

/* ===========================
   Badges de type (cohérents desktop + mobile)
   =========================== */
const TYPE_BADGE = {
  Vente: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  Donation: "bg-amber-50 text-amber-700 border border-amber-200",
  loss: "bg-red-50 text-red-700 border border-red-200",
  recovered: "bg-green-50 text-green-700 border border-green-200",
};

const Badge = ({ tone = "Vente", children }) => (
  <span
    className={`inline-flex items-center whitespace-nowrap text-[11px] font-semibold px-2.5 py-1 rounded-full uppercase tracking-wide ${
      TYPE_BADGE[tone] || TYPE_BADGE.Vente
    }`}
  >
    {children}
  </span>
);

export default function SalesDonations() {
  const { t, i18n } = useTranslation();
  const currentLocale = i18n.language || "fr";
  const locale = currentLocale.startsWith("de")
    ? "de-DE"
    : currentLocale.startsWith("fr")
      ? "fr-FR"
      : "en-GB";

  const formatDateValue = (value) => {
    const parsed = parseDateSmart(value);
    return parsed ? parsed.toLocaleDateString(locale) : value || "—";
  };

  const formatMoney = (value, currency = "EUR") => {
    const normalizedCurrency = currency === "€" ? "EUR" : currency || "EUR";
    const amount = Number(value || 0);
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: normalizedCurrency,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 2,
      }).format(amount);
    }
  };

  const transactionTypeLabel = (type) => {
    if (type === "Vente") return t("salesDonations.addModal.type.sale");
    if (type === "Donation") return t("salesDonations.addModal.type.donation");
    if (type === "Perte/Vol") return t("salesDonations.filters.transactionType.lossVol");
    return type || "—";
  };

  const lossTypeLabel = (type) => {
    const normalized = String(type || "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

    const map = {
      vol: "theft",
      theft: "theft",
      perte: "loss",
      loss: "loss",
      destruction: "destruction",
      disparition: "disappearance",
      disappearance: "disappearance",
    };

    const key = map[normalized];
    return key
      ? t(`salesDonations.losses.types.${key}`)
      : t("salesDonations.losses.types.generic");
  };

  // ⚠️ Sur certaines pages, le provider peut ne pas être monté : on garde un fallback sûr.
  const profileCtx = useContext(ProfileContext);
  const profile = profileCtx?.profile ?? null;
  const [profileRegime, setProfileRegime] = useState(null); // valeur string en BDD

  // Pertes & Vols (artwork_losses)
  const [loadingLosses, setLoadingLosses] = useState(false); // eslint-disable-line no-unused-vars
  const [losses, setLosses] = useState([]);
  const [selectedLoss, setSelectedLoss] = useState(null);
  const [showLossDetailsModal, setShowLossDetailsModal] = useState(false);
  const [recoveringId, setRecoveringId] = useState(null);

  // Modal upload
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Transactions (mock pour l’instant)
  const [transactions, setTransactions] = useState([
    {
      id: 1,
      type: "Vente",
      artwork: "Indigo",
      artist: "Clara Vermeer",
      date: "15/03/2025",
      amount: 9800,
      buyer: "Galerie Contemporaine",
      details: "Vente suite à l'exposition Winter Collection",
      taxYear: 2025,
      documents: ["Facture", "Certificat d'authenticité", "Contrat de vente"],
    },
    {
      id: 2,
      type: "Donation",
      artwork: "Mistletoe Vase",
      artist: "Studio Ceramics",
      date: "10/12/2024",
      valuationAmount: 4500,
      recipient: "Musée d'Art Moderne",
      details: "Donation dans le cadre de la collection permanente",
      taxYear: 2024,
      documents: ["Acte de donation", "Reçu fiscal", "Estimation officielle"],
    },
    {
      id: 3,
      type: "Vente",
      artwork: "From Shifting Shade",
      artist: "James Hart",
      date: "28/09/2024",
      amount: 18200,
      buyer: "Collectionneur privé",
      details: "Vente de gré à gré",
      taxYear: 2024,
      documents: ["Facture", "Certificat de provenance"],
    },
    {
      id: 4,
      type: "Donation",
      artwork: "Vase",
      artist: "Céramiques Modernes",
      date: "05/06/2024",
      valuationAmount: 3200,
      recipient: "Association Arts et Culture",
      details: "Donation pour levée de fonds annuelle",
      taxYear: 2024,
      documents: ["Acte de donation", "Reçu fiscal"],
    },
  ]);

  // Filtres
  const [transactionFilter, setTransactionFilter] = useState("all");
  const [taxYearFilter, setTaxYearFilter] = useState("all");

  // Nouvelle transaction
  const [newTransaction, setNewTransaction] = useState({
    type: "Vente",
    artwork: "",
    artist: "",
    date: new Date().toISOString().split("T")[0],
    amount: "",
    valuationAmount: "",
    buyer: "",
    recipient: "",
    details: "",
    taxYear: new Date().getFullYear(),
    documents: [],
  });

  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const [selectedExportYear, setSelectedExportYear] = useState("Toutes");

  // Régime fiscal sélectionné par profil
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let ignore = false;

    (async () => {
      if (profile && typeof profile.fiscal_regime !== "undefined") {
        if (!ignore) setProfileRegime(profile.fiscal_regime || null);
        return;
      }
      // fallback direct (rare)
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("fiscal_regime")
          .limit(1);
        if (!ignore && !error) {
          setProfileRegime(data?.[0]?.fiscal_regime || null);
        }
      } catch {
        // on restera en "aucun régime" (simple tableau)
      }
    })();

    return () => {
      ignore = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.fiscal_regime]);

  /* ===========================
     Gestion formulaire
     =========================== */

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewTransaction((prev) => ({
      ...prev,
      [name]:
        name === "amount" || name === "valuationAmount"
          ? value === "" ? "" : parseFloat(value)
          : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const transactionToAdd = {
      id: transactions.length + 1,
      ...newTransaction,
      date: new Date(newTransaction.date).toLocaleDateString(
        locale
      ),
      documents: [],
    };

    setTransactions([transactionToAdd, ...transactions]);
    setNewTransaction({
      type: "Vente",
      artwork: "",
      artist: "",
      date: new Date().toISOString().split("T")[0],
      amount: "",
      valuationAmount: "",
      buyer: "",
      recipient: "",
      details: "",
      taxYear: new Date().getFullYear(),
      documents: [],
    });
    setShowAddModal(false);
  };

  // Chargement des pertes & vols
  useEffect(() => {
    let ignore = false;
    const load = async () => {
      setLoadingLosses(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session || ignore) return;
        const { data, error } = await supabase
          .from("artwork_losses")
          .select("*")
          .eq("client_id", session.user.id)
          .order("loss_date", { ascending: false });
        if (!ignore && !error) setLosses(data || []);
      } catch (e) {
        console.error("SalesDonations: chargement pertes", e);
      } finally {
        if (!ignore) setLoadingLosses(false);
      }
    };
    load();
    return () => { ignore = true; };
  }, []);

  // Marquer une œuvre comme retrouvée
  const handleMarkRecovered = async (loss) => {
    if (!loss?.id) return;
    setRecoveringId(loss.id);
    try {
      const recoveredAt = new Date().toISOString().split("T")[0];
      const { error: lossErr } = await supabase
        .from("artwork_losses")
        .update({ recovered_at: recoveredAt })
        .eq("id", loss.id);
      if (lossErr) throw lossErr;

      // Remettre l'œuvre disponible si on a un artwork_id
      if (loss.artwork_id) {
        await supabase
          .from("artworks")
          .update({ availability: "available" })
          .eq("id", loss.artwork_id);
      }

      setLosses((prev) =>
        prev.map((l) => l.id === loss.id ? { ...l, recovered_at: recoveredAt } : l)
      );
      setSelectedLoss((prev) => prev ? { ...prev, recovered_at: recoveredAt } : prev);
    } catch (e) {
      console.error("Erreur récupération:", e);
      alert(t("salesDonations.errors.updateLoss", { message: e.message }));
    } finally {
      setRecoveringId(null);
    }
  };

  /* ===========================
     Filtres & agrégats
     =========================== */

  const filteredTransactions = transactions.filter((transaction) => {
    if (transactionFilter !== "all" && transaction.type !== transactionFilter)
      return false;
    if (
      taxYearFilter !== "all" &&
      transaction.taxYear !== parseInt(taxYearFilter, 10)
    )
      return false;
    return true;
  });

  // Pertes filtrées (non récupérées = potentiellement déductibles)
  const filteredLosses = losses.filter((l) => {
    if (transactionFilter !== "all" && transactionFilter !== "Perte/Vol") return false;
    const lossYear = l.tax_year || new Date(l.loss_date).getFullYear();
    if (taxYearFilter !== "all" && String(lossYear) !== taxYearFilter) return false;
    return true;
  });

  const calculateTaxYearTotals = () => {
    const txYears = transactions.map((t) => t.taxYear);
    const lossYears = losses.map((l) => l.tax_year || new Date(l.loss_date).getFullYear());
    const years = [...new Set([...txYears, ...lossYears])].sort();
    return years.map((year) => {
      const yearTransactions = transactions.filter((t) => t.taxYear === year);
      const salesTotal = yearTransactions
        .filter((t) => t.type === "Vente")
        .reduce((sum, t) => sum + Number(t.amount || 0), 0);
      const donationsTotal = yearTransactions
        .filter((t) => t.type === "Donation")
        .reduce((sum, t) => sum + Number(t.valuationAmount || 0), 0);

      // Pertes déductibles = plainte déposée + non récupérées
      const yearLosses = losses.filter((l) => (l.tax_year || new Date(l.loss_date).getFullYear()) === year);
      const deductibleLosses = yearLosses.filter((l) => l.police_report && !l.recovered_at);
      const lossesDeductible = deductibleLosses.reduce((s, l) => s + (Number(l.declared_value) || 0), 0);
      const lossesTotal = yearLosses.reduce((s, l) => s + (Number(l.declared_value) || 0), 0);
      const recoveredCount = yearLosses.filter((l) => l.recovered_at).length;

      return {
        year,
        salesTotal,
        donationsTotal,
        total: salesTotal + donationsTotal,
        lossesTotal,
        lossesDeductible,
        lossesCount: yearLosses.length,
        recoveredCount,
      };
    });
  };

  const taxYearTotals = calculateTaxYearTotals();
  const hasAnyLoss = losses.length > 0;

  /* ===========================
     Helpers i18n pour PDF
     =========================== */

  const prettyRegime = (regime) => {
    switch (regime) {
      case REGIMES.NONE:
        return t("salesDonations.regime.none");
      case REGIMES.FR_FORFAIT_6_5:
        return t("salesDonations.regime.frForfait");
      case REGIMES.FR_PLUS_VALUE:
        return t("salesDonations.regime.frPlusValue");
      case REGIMES.UK_CGT_SIMPLE_20:
        return t("salesDonations.regime.uk");
      case REGIMES.DE_PRIVATE_1Y_RULE:
        return t("salesDonations.regime.de");
      default:
        return regime || t("salesDonations.regime.none");
    }
  };

  const buildYearSnapshot = (year) => {
    const tx = transactions.filter((t) => String(t.taxYear) === String(year));
    const ventes = tx.filter(
      (t) => (t.type || "").toLowerCase() === "vente"
    );
    const dons = tx.filter(
      (t) => (t.type || "").toLowerCase() === "donation"
    );
    const salesTotal = ventes.reduce(
      (s, t) => s + Number(t.amount || 0),
      0
    );
    const donationsTotal = dons.reduce(
      (s, t) => s + Number(t.valuationAmount || 0),
      0
    );
    const total = salesTotal + donationsTotal;

    const regime = profileRegime || REGIMES.NONE;

    const frPart = computeYearTaxFR({ regime, salesInYear: ventes });
    const intlPart = computeYearTaxINTL({ regime, salesInYear: ventes });
    const estTax = frPart ?? intlPart;

    return { ventes, dons, salesTotal, donationsTotal, total, regime, estTax };
  };

  const renderPdfHTMLForYears = (years) => {
    const yearsHtml = years
      .map((year) => {
        const s = buildYearSnapshot(year);
        return `
        <div class="section">
          <h3>${t("salesDonations.pdf.yearTitle", { year })}</h3>
          <table>
            <thead>
              <tr>
                <th>${t("salesDonations.pdf.column.section")}</th>
                <th class="right">${t(
                  "salesDonations.pdf.column.amount"
                )}</th>
                <th class="right">${t(
                  "salesDonations.pdf.column.count"
                )}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>${t("salesDonations.pdf.row.sales")}</td>
                <td class="right">${s.salesTotal.toLocaleString(
                  locale
                )}</td>
                <td class="right">${s.ventes.length}</td>
              </tr>
              <tr>
                <td>${t("salesDonations.pdf.row.donations")}</td>
                <td class="right">${s.donationsTotal.toLocaleString(
                  locale
                )}</td>
                <td class="right">${s.dons.length}</td>
              </tr>
              <tr>
                <td><strong>${t(
                  "salesDonations.pdf.row.totalOperations"
                )}</strong></td>
                <td class="right"><strong>${s.total.toLocaleString(
                  locale
                )}</strong></td>
                <td class="right">—</td>
              </tr>
              <tr>
                <td>${t("salesDonations.pdf.row.regime")}</td>
                <td colspan="2"><span class="badge">${prettyRegime(
                  s.regime
                )}</span></td>
              </tr>
              <tr>
                <td>${t("salesDonations.pdf.row.estimatedTax")}</td>
                <td class="right">${
                  s.regime === REGIMES.NONE || s.estTax === null
                    ? "—"
                    : Math.round(s.estTax).toLocaleString(
                        locale
                      ) + " €"
                }</td>
                <td class="right">—</td>
              </tr>
            </tbody>
          </table>

          <h4 class="sub">${t(
            "salesDonations.pdf.salesDetails.title"
          )}</h4>
          <table>
            <thead>
              <tr>
                <th>${t("salesDonations.pdf.salesDetails.date")}</th>
                <th>${t("salesDonations.pdf.salesDetails.artist")}</th>
                <th>${t("salesDonations.pdf.salesDetails.artwork")}</th>
                <th>${t("salesDonations.pdf.salesDetails.buyer")}</th>
                <th class="right">${t(
                  "salesDonations.pdf.salesDetails.price"
                )}</th>
                <th>${t(
                  "salesDonations.pdf.salesDetails.purchasePrice"
                )}</th>
                <th>${t(
                  "salesDonations.pdf.salesDetails.purchaseDate"
                )}</th>
              </tr>
            </thead>
            <tbody>
              ${
                s.ventes.length === 0
                  ? `<tr><td colspan="7" class="muted">${t(
                      "salesDonations.pdf.salesDetails.none",
                      { year }
                    )}</td></tr>`
                  : s.ventes
                      .map(
                        (v) => `
                    <tr>
                      <td>${v.date || "—"}</td>
                      <td>${v.artist || "—"}</td>
                      <td>${v.artwork || "—"}</td>
                      <td>${v.buyer || "—"}</td>
                      <td class="right">${Number(
                        v.amount || 0
                      ).toLocaleString(
                        locale
                      )}</td>
                      <td>${
                        v.acquisition_price
                          ? Number(
                              v.acquisition_price
                            ).toLocaleString(
                              locale
                            ) + " €"
                          : "—"
                      }</td>
                      <td>${v.acquisition_date || "—"}</td>
                    </tr>`
                      )
                      .join("")
              }
            </tbody>
          </table>

          <h4 class="sub">${t(
            "salesDonations.pdf.donationsDetails.title"
          )}</h4>
          <table>
            <thead>
              <tr>
                <th>${t("salesDonations.pdf.donationsDetails.date")}</th>
                <th>${t("salesDonations.pdf.donationsDetails.artist")}</th>
                <th>${t("salesDonations.pdf.donationsDetails.artwork")}</th>
                <th>${t(
                  "salesDonations.pdf.donationsDetails.recipient"
                )}</th>
                <th class="right">${t(
                  "salesDonations.pdf.donationsDetails.estimatedValue"
                )}</th>
              </tr>
            </thead>
            <tbody>
              ${
                s.dons.length === 0
                  ? `<tr><td colspan="5" class="muted">${t(
                      "salesDonations.pdf.donationsDetails.none",
                      { year }
                    )}</td></tr>`
                  : s.dons
                      .map(
                        (d) => `
                    <tr>
                      <td>${d.date || "—"}</td>
                      <td>${d.artist || "—"}</td>
                      <td>${d.artwork || "—"}</td>
                      <td>${d.recipient || "—"}</td>
                      <td class="right">${Number(
                        d.valuationAmount || 0
                      ).toLocaleString(
                        locale
                      )}</td>
                    </tr>`
                      )
                      .join("")
              }
            </tbody>
          </table>

          <div class="muted note">
            ${
              s.regime === REGIMES.FR_FORFAIT_6_5
                ? t("salesDonations.pdf.notes.frForfait")
                : s.regime === REGIMES.FR_PLUS_VALUE
                ? t("salesDonations.pdf.notes.frPlusValue")
                : s.regime === REGIMES.UK_CGT_SIMPLE_20
                ? t("salesDonations.pdf.notes.uk")
                : s.regime === REGIMES.DE_PRIVATE_1Y_RULE
                ? t("salesDonations.pdf.notes.de")
                : t("salesDonations.pdf.notes.none")
            }
          </div>
        </div>
      `;
      })
      .join("");

    const title =
      selectedExportYear === "Toutes"
        ? t("salesDonations.pdf.titleAllYears")
        : t("salesDonations.pdf.titleYear", {
            year: selectedExportYear,
          });

    return `
      <div id="pdf-root" class="p-6 bg-white text-black" style="width:794px;">
        <style>
          #pdf-root { font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial; }
          #pdf-root h2 { font-size: 18px; margin: 0 0 14px 0; }
          #pdf-root h3 { font-size: 16px; margin: 18px 0 8px 0; }
          #pdf-root .sub { font-size: 13px; margin: 12px 0 6px 0; }
          #pdf-root .muted { color: #6b7280; font-size: 12px; }
          #pdf-root table { width: 100%; border-collapse: collapse; margin: 8px 0 14px 0; font-size: 12px; }
          #pdf-root th, #pdf-root td { border: 1px solid #e5e7eb; padding: 6px 8px; text-align: left; vertical-align: top; }
          #pdf-root th { background: #f9fafb; font-weight: 600; }
          #pdf-root .right { text-align: right; }
          #pdf-root .section { border: 1px solid #e5e7eb; padding: 10px; margin-bottom: 12px; }
          #pdf-root .badge { display:inline-block; padding:2px 6px; border:1px solid #e5e7eb; background:#f8f8f8; font-size:11px; }
          #pdf-root .disclaimer { font-size: 11px; color:#374151; line-height:1.35; border-top:1px solid #e5e7eb; padding-top:10px; margin-top:10px; }
        </style>
        <h2>${title}</h2>
        <div class="muted">${t("salesDonations.pdf.generatedFromArtkiv")}</div>
        ${yearsHtml}
        <div class="disclaimer">
          <strong>${t(
            "salesDonations.pdf.disclaimer.title"
          )}</strong> ${t("salesDonations.pdf.disclaimer.body")}
        </div>
      </div>
    `;
  };

  const exportHtmlToPdf = async (html, filename) => {
    const mount = document.createElement("div");
    mount.id = "pdf-runtime";
    mount.style.position = "fixed";
    mount.style.left = "-99999px";
    mount.style.top = "0";
    mount.style.width = "794px";
    mount.innerHTML = html;
    document.body.appendChild(mount);

    const content = mount.querySelector("#pdf-root");
    try {
      const canvas = await html2canvas(content, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(filename);
    } catch (e) {
      console.error("Export PDF échoué:", e);
      alert(t("salesDonations.pdf.error"));
    } finally {
      if (mount && mount.parentNode) {
        mount.parentNode.removeChild(mount);
      }
    }
  };

  const handleExportAnnualReport = async () => {
    const years =
      selectedExportYear === "Toutes"
        ? [...new Set(transactions.map((t) => t.taxYear))].sort(
            (a, b) => b - a
          )
        : [Number(selectedExportYear)];
    const html = renderPdfHTMLForYears(years);
    await exportHtmlToPdf(
      html,
      `rapport_fiscal_${selectedExportYear}.pdf`
    );
  };

  const handleExportSingleYear = async (year) => {
    const html = renderPdfHTMLForYears([Number(year)]);
    await exportHtmlToPdf(html, `rapport_fiscal_${year}.pdf`);
  };

  /* ===========================
     Rendu
     =========================== */

  const allYears = [...new Set([
    ...transactions.map((t) => t.taxYear),
    ...losses.map((l) => l.tax_year || new Date(l.loss_date).getFullYear()),
  ])].sort((a, b) => b - a);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
      {/* Header + export global */}
      <div className="mb-8">
        <p className="text-xs tracking-widest text-gray-400 uppercase mb-1">{t("salesDonations.eyebrow")}</p>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <h1 className="text-3xl font-serif text-gray-900">
            {t("salesDonations.title")}
          </h1>

          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("salesDonations.export.yearToGenerate")}
              </label>
              <select
                value={selectedExportYear}
                onChange={(e) => setSelectedExportYear(e.target.value)}
                className="border border-gray-300 px-3 py-2 rounded text-sm w-full sm:w-auto min-w-[140px]"
              >
                <option value="Toutes">
                  {t("salesDonations.export.yearAll")}
                </option>
                {allYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            <button
              className="btn-artsy btn-primary-artsy whitespace-nowrap self-start sm:self-auto"
              onClick={handleExportAnnualReport}
            >
              {t("salesDonations.export.button")}
            </button>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap gap-4 mb-8">
        <div>
          <label
            htmlFor="transactionFilter"
            className="block text-sm font-medium text-gray-dark mb-1"
          >
            {t("salesDonations.filters.transactionType.label")}
          </label>
          <select
            id="transactionFilter"
            value={transactionFilter}
            onChange={(e) => setTransactionFilter(e.target.value)}
            className="border border-gray-medium p-2 min-w-[150px]"
          >
            <option value="all">
              {t("salesDonations.filters.transactionType.all")}
            </option>
            <option value="Vente">
              {t("salesDonations.filters.transactionType.sale")}
            </option>
            <option value="Donation">
              {t("salesDonations.filters.transactionType.donation")}
            </option>
            <option value="Perte/Vol">{t("salesDonations.filters.transactionType.lossVol")}</option>
          </select>
        </div>

        <div>
          <label
            htmlFor="taxYearFilter"
            className="block text-sm font-medium text-gray-dark mb-1"
          >
            {t("salesDonations.filters.taxYear.label")}
          </label>
          <select
            id="taxYearFilter"
            value={taxYearFilter}
            onChange={(e) => setTaxYearFilter(e.target.value)}
            className="border border-gray-medium p-2 min-w-[150px]"
          >
            <option value="all">
              {t("salesDonations.filters.taxYear.all")}
            </option>
            {allYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Résumé fiscal par année */}
      <div className="mb-8">
        <h2 className="text-xl font-serif mb-4">
          {t("salesDonations.summary.title")}
        </h2>

        {!profileRegime && (
          <div className="mb-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            {t("salesDonations.summary.noRegime")}
          </div>
        )}

        {/* Table desktop */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-medium">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-dark tracking-wider">
                  {t("salesDonations.summary.col.year")}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-dark tracking-wider">
                  {t("salesDonations.summary.col.sales")}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-dark tracking-wider">
                  {t("salesDonations.summary.col.donations")}
                </th>
                {hasAnyLoss && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-dark tracking-wider">
                    <span className="flex items-center gap-1">
                      {t("salesDonations.summary.col.losses")}
                      <span
                        title={t("salesDonations.summary.col.lossesTooltip")}
                        className="text-gray-400 cursor-help text-xs"
                      >
                        ⓘ
                      </span>
                    </span>
                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-dark tracking-wider">
                  {t("salesDonations.summary.col.total")}
                </th>
                <th className="px-4 py-3 relative">
                  <span className="sr-only">{t("salesDonations.summary.col.actions")}</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-medium">
              {taxYearTotals.map((yearData) => {
                const salesOfThisYear = transactions.filter(
                  (t) => String(t.taxYear) === String(yearData.year)
                );
                const chosen = profileRegime || REGIMES.NONE;
                const frPart = computeYearTaxFR({ regime: chosen, salesInYear: salesOfThisYear });
                const intlPart = computeYearTaxINTL({ regime: chosen, salesInYear: salesOfThisYear });
                const estTax = frPart ?? intlPart;

                return (
                  <tr key={yearData.year} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium">{yearData.year}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm">{formatMoney(yearData.salesTotal)}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm">{formatMoney(yearData.donationsTotal)}</div>
                    </td>
                    {hasAnyLoss && (
                      <td className="px-4 py-3 whitespace-nowrap">
                        {yearData.lossesCount > 0 ? (
                          <div>
                            <div className="text-sm font-medium text-red-700">
                              {yearData.lossesDeductible > 0
                                ? `${t("salesDonations.losses.deductionPrefix")} ${formatMoney(yearData.lossesDeductible)}`
                                : "—"}
                            </div>
                            <div className="text-xs text-gray-400">
                              {t("salesDonations.losses.events", { count: yearData.lossesCount })}
                              {yearData.recoveredCount > 0 &&
                                ` · ${t("salesDonations.losses.recovered", {
                                  count: yearData.recoveredCount,
                                })}`}
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm text-gray-400">—</div>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3 whitespace-nowrap">
                      {chosen === REGIMES.NONE || estTax === null ? (
                        <div className="text-sm font-medium">{formatMoney(yearData.total)}</div>
                      ) : (
                        <>
                          <div className="text-sm font-medium">{t("salesDonations.summary.estimatedTax")} {formatMoney(Math.round(estTax))}</div>
                          <div className="text-xs text-gray-500">
                            {chosen === REGIMES.FR_FORFAIT_6_5 ? t("salesDonations.summary.regimeLabel.frForfait") : t("salesDonations.summary.regimeLabel.frPlusValue")}
                          </div>
                        </>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                      <button className="text-gray-dark hover:text-black" onClick={() => handleExportSingleYear(yearData.year)}>
                        {t("salesDonations.summary.exportYear")}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Cartes mobile */}
        <div className="md:hidden space-y-3">
          {taxYearTotals.map((yearData) => {
            const chosen = profileRegime || REGIMES.NONE;
            const salesOfThisYear = transactions.filter((t) => String(t.taxYear) === String(yearData.year));
            const frPart = computeYearTaxFR({ regime: chosen, salesInYear: salesOfThisYear });
            const intlPart = computeYearTaxINTL({ regime: chosen, salesInYear: salesOfThisYear });
            const estTax = frPart ?? intlPart;

            return (
              <div key={yearData.year} className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-base font-semibold text-gray-900">{yearData.year}</span>
                  <button className="text-xs text-gray-500 hover:text-black underline" onClick={() => handleExportSingleYear(yearData.year)}>
                    {t("salesDonations.summary.exportYear")}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">{t("salesDonations.summary.col.sales")}</p>
                    <p className="font-medium text-gray-900">{formatMoney(yearData.salesTotal)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">{t("salesDonations.summary.col.donations")}</p>
                    <p className="font-medium text-gray-900">{formatMoney(yearData.donationsTotal)}</p>
                  </div>
                  {hasAnyLoss && yearData.lossesCount > 0 && (
                    <div className="col-span-2">
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">{t("salesDonations.summary.col.losses")}</p>
                      <p className="font-medium text-red-700">
                        {yearData.lossesDeductible > 0
                          ? `${t("salesDonations.losses.deductionPrefix")} ${formatMoney(yearData.lossesDeductible)}`
                          : "—"}
                        <span className="text-xs text-gray-400 ml-2 font-normal">
                          {t("salesDonations.losses.events", { count: yearData.lossesCount })}
                          {yearData.recoveredCount > 0 &&
                            ` · ${t("salesDonations.losses.recovered", {
                              count: yearData.recoveredCount,
                            })}`}
                        </span>
                      </p>
                    </div>
                  )}
                  <div className="col-span-2 border-t border-gray-100 pt-2 mt-1">
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">{t("salesDonations.summary.col.total")}</p>
                    {chosen === REGIMES.NONE || estTax === null ? (
                      <p className="font-semibold text-gray-900">{formatMoney(yearData.total)}</p>
                    ) : (
                      <p className="font-semibold text-gray-900">{t("salesDonations.summary.estimatedTax")} {formatMoney(Math.round(estTax))}</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Historique des transactions */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-serif">
            {t("salesDonations.history.title")}
          </h2>
          <button
            className="btn-artsy btn-secondary-artsy"
            onClick={() => setShowAddModal(true)}
          >
            {t("salesDonations.history.addTransaction")}
          </button>
        </div>

        {/* Table desktop */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-medium">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-dark tracking-wider">{t("salesDonations.history.col.type")}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-dark tracking-wider">{t("salesDonations.history.col.date")}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-dark tracking-wider">{t("salesDonations.history.col.artwork")}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-dark tracking-wider">{t("salesDonations.history.col.artist")}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-dark tracking-wider">{t("salesDonations.history.col.amount")}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-dark tracking-wider">{t("salesDonations.history.col.counterparty")}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-dark tracking-wider">{t("salesDonations.history.col.police")}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-dark tracking-wider">{t("salesDonations.history.col.taxYear")}</th>
                <th className="px-4 py-3 relative"><span className="sr-only">{t("salesDonations.history.col.actions")}</span></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-medium">
              {/* Ventes & donations */}
              {(transactionFilter === "all" || transactionFilter === "Vente" || transactionFilter === "Donation") &&
                filteredTransactions.map((transaction) => (
                  <tr key={`tx-${transaction.id}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">
                      <Badge tone={transaction.type === "Vente" ? "Vente" : "Donation"}>
                        {transactionTypeLabel(transaction.type)}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{formatDateValue(transaction.date)}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{transaction.artwork}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{transaction.artist}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {formatMoney(transaction.amount || transaction.valuationAmount || 0)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{transaction.type === "Vente" ? transaction.buyer : transaction.recipient}</td>
                    <td className="px-4 py-3 text-sm text-gray-300 text-center">—</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{transaction.taxYear}</td>
                    <td className="px-4 py-3 text-right text-sm">
                      <button className="text-gray-dark hover:text-black" onClick={() => { setSelectedTransaction(transaction); setShowDetailsModal(true); }}>
                        {t("salesDonations.history.viewDetails")}
                      </button>
                    </td>
                  </tr>
                ))
              }
              {/* Pertes & vols */}
              {(transactionFilter === "all" || transactionFilter === "Perte/Vol") &&
                filteredLosses.map((loss) => {
                  const lossYear = loss.tax_year || new Date(loss.loss_date).getFullYear();
                  const isRecovered = !!loss.recovered_at;
                  return (
                    <tr key={`loss-${loss.id}`} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <Badge tone={isRecovered ? "recovered" : "loss"}>
                          {isRecovered ? t("salesDonations.losses.badge.recovered") : lossTypeLabel(loss.loss_type)}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{formatDateValue(loss.loss_date)}</td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{loss.artwork_title || "—"}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{loss.artist || "—"}</td>
                      <td className="px-4 py-3 text-sm font-medium">
                        {loss.declared_value ? (
                          <span className="text-red-700">−{formatMoney(loss.declared_value, loss.currency)}</span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-300 text-center">—</td>
                      <td className="px-4 py-3">
                        {loss.police_report ? (
                          <span className="text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full border border-green-200">
                            {t("salesDonations.losses.badge.policeYes")}
                          </span>
                        ) : (
                          <span className="text-xs font-medium text-gray-500 bg-gray-50 px-2 py-0.5 rounded-full border border-gray-200">
                            {t("salesDonations.losses.badge.policeNo")}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{lossYear}</td>
                      <td className="px-4 py-3 text-right text-sm">
                        <button className="text-gray-dark hover:text-black" onClick={() => { setSelectedLoss(loss); setShowLossDetailsModal(true); }}>
                          {t("salesDonations.history.viewDetails")}
                        </button>
                      </td>
                    </tr>
                  );
                })
              }
              {filteredTransactions.length === 0 && filteredLosses.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-400 text-sm">{t("salesDonations.history.noEvent")}</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Cartes mobile */}
        <div className="md:hidden space-y-3">
          {filteredTransactions.length === 0 && filteredLosses.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-lg p-6 text-center text-gray-400 text-sm">
              {t("salesDonations.history.noTransaction")}
            </div>
          ) : (
            <>
              {/* Ventes & donations */}
              {(transactionFilter === "all" || transactionFilter === "Vente" || transactionFilter === "Donation") &&
                filteredTransactions.map((transaction) => {
                  const isVente = transaction.type === "Vente";
                  const amount = Number(transaction.amount || transaction.valuationAmount || 0);
                  return (
                    <div key={`tx-${transaction.id}`} className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <Badge tone={isVente ? "Vente" : "Donation"}>
                          {transactionTypeLabel(transaction.type)}
                        </Badge>
                        <span className="text-xs text-gray-400">{formatDateValue(transaction.date)}</span>
                      </div>
                      <p className="text-sm font-medium text-gray-900 mb-0.5">{transaction.artwork}</p>
                      {transaction.artist && <p className="text-sm text-gray-500 mb-3">{transaction.artist}</p>}
                      <div className="flex items-center justify-between text-sm border-t border-gray-100 pt-3">
                        <div>
                          <p className="text-xs text-gray-400 mb-0.5">{isVente ? transaction.buyer : transaction.recipient}</p>
                          <p className="font-semibold text-gray-900">{formatMoney(amount)}</p>
                        </div>
                        <button className="text-xs text-gray-500 hover:text-black underline" onClick={() => { setSelectedTransaction(transaction); setShowDetailsModal(true); }}>
                          {t("salesDonations.history.viewDetails")}
                        </button>
                      </div>
                    </div>
                  );
                })
              }
              {/* Pertes & vols mobile */}
              {(transactionFilter === "all" || transactionFilter === "Perte/Vol") &&
                filteredLosses.map((loss) => {
                  const isRecovered = !!loss.recovered_at;
                  const lossYear = loss.tax_year || new Date(loss.loss_date).getFullYear();
                  return (
                    <div key={`loss-${loss.id}`} className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <Badge tone={isRecovered ? "recovered" : "loss"}>
                          {isRecovered ? t("salesDonations.losses.badge.recovered") : lossTypeLabel(loss.loss_type)}
                        </Badge>
                        <span className="text-xs text-gray-400">{formatDateValue(loss.loss_date)}</span>
                      </div>
                      <p className="text-sm font-medium text-gray-900 mb-0.5">{loss.artwork_title || "—"}</p>
                      {loss.artist && <p className="text-sm text-gray-500 mb-2">{loss.artist}</p>}
                      <div className="flex items-center gap-2 mb-3">
                        {loss.police_report ? (
                          <span className="text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full border border-green-200">
                            {t("salesDonations.losses.badge.withPolice")}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400 border border-gray-200 px-2 py-0.5 rounded-full">
                            {t("salesDonations.losses.badge.noPolice")}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between text-sm border-t border-gray-200 pt-3">
                        <div>
                          <p className="text-xs text-gray-400 mb-0.5">{t("salesDonations.losses.taxYear", { year: lossYear })}</p>
                          <p className="font-semibold text-red-700">
                            {loss.declared_value ? `−${formatMoney(loss.declared_value, loss.currency)}` : "—"}
                          </p>
                        </div>
                        <button className="text-xs text-gray-500 hover:text-black underline" onClick={() => { setSelectedLoss(loss); setShowLossDetailsModal(true); }}>
                          {t("salesDonations.history.viewDetails")}
                        </button>
                      </div>
                    </div>
                  );
                })
              }
            </>
          )}
        </div>
      </div>

      {/* Modale détail perte/vol */}
      {showLossDetailsModal && selectedLoss && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4" onClick={() => setShowLossDetailsModal(false)}>
          <div className="bg-white rounded-lg max-w-md w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-5">
              <h3 className="font-serif text-xl text-gray-900">{t("salesDonations.losses.modal.title")}</h3>
              <button onClick={() => setShowLossDetailsModal(false)} className="text-gray-400 hover:text-black">✕</button>
            </div>

            <div className="space-y-3 text-sm">
              {[
                [t("salesDonations.losses.modal.fields.type"), lossTypeLabel(selectedLoss.loss_type)],
                [t("salesDonations.losses.modal.fields.artwork"), selectedLoss.artwork_title],
                [t("salesDonations.losses.modal.fields.artist"), selectedLoss.artist],
                [t("salesDonations.losses.modal.fields.date"), formatDateValue(selectedLoss.loss_date)],
                [
                  t("salesDonations.losses.modal.fields.declaredValue"),
                  selectedLoss.declared_value
                    ? formatMoney(selectedLoss.declared_value, selectedLoss.currency)
                    : "—",
                ],
                [
                  t("salesDonations.losses.modal.fields.indemnity"),
                  selectedLoss.indemnity
                    ? formatMoney(selectedLoss.indemnity, selectedLoss.currency)
                    : "—",
                ],
                [t("salesDonations.losses.modal.fields.taxYear"), selectedLoss.tax_year],
                [
                  t("salesDonations.losses.modal.fields.location"),
                  selectedLoss.location_at_loss || "—",
                ],
              ].map(([label, val]) => (
                <div key={label} className="flex justify-between border-b border-gray-100 pb-2">
                  <span className="font-medium text-gray-600">{label}</span>
                  <span className="text-gray-900">{val || "—"}</span>
                </div>
              ))}

              {/* Plainte */}
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <span className="font-medium text-gray-600">{t("salesDonations.losses.modal.fields.policeReport")}</span>
                {selectedLoss.police_report
                  ? (
                    <span className="text-green-700 font-medium">
                      {t("salesDonations.losses.modal.policeYes")}
                      {selectedLoss.police_ref
                        ? ` — ${t("salesDonations.losses.modal.reference", {
                            reference: selectedLoss.police_ref,
                          })}`
                        : ""}
                    </span>
                  ) : (
                    <span className="text-gray-400">
                      {t("salesDonations.losses.modal.policeNo")}
                    </span>
                  )}
              </div>

              {/* Assurance */}
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <span className="font-medium text-gray-600">{t("salesDonations.losses.modal.fields.insuranceClaim")}</span>
                {selectedLoss.insurance_claim
                  ? (
                    <span className="text-blue-700 font-medium">
                      {t("salesDonations.losses.modal.insuranceYes")}
                      {selectedLoss.insurance_ref
                        ? ` — ${t("salesDonations.losses.modal.reference", {
                            reference: selectedLoss.insurance_ref,
                          })}`
                        : ""}
                    </span>
                  ) : (
                    <span className="text-gray-400">
                      {t("salesDonations.losses.modal.insuranceNo")}
                    </span>
                  )}
              </div>

              {/* Récupération */}
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <span className="font-medium text-gray-600">{t("salesDonations.losses.modal.fields.status")}</span>
                {selectedLoss.recovered_at
                  ? (
                    <span className="text-green-700 font-medium">
                      {t("salesDonations.losses.modal.recovered", {
                        date: formatDateValue(selectedLoss.recovered_at),
                      })}
                    </span>
                  ) : (
                    <span className="text-red-700">
                      {t("salesDonations.losses.modal.notRecovered")}
                    </span>
                  )}
              </div>

              {selectedLoss.notes && (
                <div className="pt-1">
                  <p className="font-medium text-gray-600 mb-1">{t("salesDonations.losses.modal.fields.notes")}</p>
                  <p className="text-gray-700 text-xs leading-relaxed">{selectedLoss.notes}</p>
                </div>
              )}
            </div>

            {/* Note fiscale */}
            {!selectedLoss.recovered_at && (
              <div className={`mt-4 rounded p-3 text-xs leading-relaxed ${selectedLoss.police_report ? "bg-green-50 border border-green-200 text-green-800" : "bg-amber-50 border border-amber-200 text-amber-800"}`}>
                {selectedLoss.police_report
                  ? t("salesDonations.losses.modal.fiscalNoteWithPolice")
                  : t("salesDonations.losses.modal.fiscalNoteNoPolice")}
              </div>
            )}

            <div className="mt-6 flex justify-between gap-3">
              <button onClick={() => setShowLossDetailsModal(false)} className="btn-artsy btn-secondary-artsy text-sm">
                {t("common.close")}
              </button>
              {!selectedLoss.recovered_at && (
                <button
                  onClick={() => handleMarkRecovered(selectedLoss)}
                  disabled={recoveringId === selectedLoss.id}
                  className="btn-artsy btn-primary-artsy text-sm"
                  style={{ background: "#166534" }}
                >
                  {recoveringId === selectedLoss.id ? t("salesDonations.losses.modal.recovering") : t("salesDonations.losses.modal.ctaRecover")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal ajout de transaction */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-md max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <h3 className="font-serif text-xl">
                {t("salesDonations.addModal.title")}
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-500 hover:text-black"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="type"
                    className="block text-sm font-medium text-gray-dark mb-1"
                  >
                    {t("salesDonations.addModal.field.type")}
                  </label>
                  <select
                    id="type"
                    name="type"
                    value={newTransaction.type}
                    onChange={handleInputChange}
                    className="w-full border border-gray-medium p-2"
                  >
                    <option value="Vente">
                      {t("salesDonations.addModal.type.sale")}
                    </option>
                    <option value="Donation">
                      {t("salesDonations.addModal.type.donation")}
                    </option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="artwork"
                    className="block text-sm font-medium text-gray-dark mb-1"
                  >
                    {t("salesDonations.addModal.field.artwork")}
                  </label>
                  <input
                    type="text"
                    id="artwork"
                    name="artwork"
                    value={newTransaction.artwork}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-gray-medium p-2"
                  />
                </div>

                <div>
                  <label
                    htmlFor="artist"
                    className="block text-sm font-medium text-gray-dark mb-1"
                  >
                    {t("salesDonations.addModal.field.artist")}
                  </label>
                  <input
                    type="text"
                    id="artist"
                    name="artist"
                    value={newTransaction.artist}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-gray-medium p-2"
                  />
                </div>

                <div>
                  <label
                    htmlFor="date"
                    className="block text-sm font-medium text-gray-dark mb-1"
                  >
                    {t("salesDonations.addModal.field.date")}
                  </label>
                  <input
                    type="date"
                    id="date"
                    name="date"
                    value={newTransaction.date}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-gray-medium p-2"
                  />
                </div>

                {newTransaction.type === "Vente" ? (
                  <div>
                    <label
                      htmlFor="amount"
                      className="block text-sm font-medium text-gray-dark mb-1"
                    >
                      {t("salesDonations.addModal.field.amount")}
                    </label>
                    <input
                      type="number"
                      id="amount"
                      name="amount"
                      value={newTransaction.amount}
                      onChange={handleInputChange}
                      required
                      min="0"
                      step="0.01"
                      className="w-full border border-gray-medium p-2"
                    />
                  </div>
                ) : (
                  <div>
                    <label
                      htmlFor="valuationAmount"
                      className="block text-sm font-medium text-gray-dark mb-1"
                    >
                      {t("salesDonations.addModal.field.valuation")}
                    </label>
                    <input
                      type="number"
                      id="valuationAmount"
                      name="valuationAmount"
                      value={newTransaction.valuationAmount}
                      onChange={handleInputChange}
                      required
                      min="0"
                      step="0.01"
                      className="w-full border border-gray-medium p-2"
                    />
                  </div>
                )}

                {newTransaction.type === "Vente" ? (
                  <div>
                    <label
                      htmlFor="buyer"
                      className="block text-sm font-medium text-gray-dark mb-1"
                    >
                      {t("salesDonations.addModal.field.buyer")}
                    </label>
                    <input
                      type="text"
                      id="buyer"
                      name="buyer"
                      value={newTransaction.buyer}
                      onChange={handleInputChange}
                      required
                      className="w-full border border-gray-medium p-2"
                    />
                  </div>
                ) : (
                  <div>
                    <label
                      htmlFor="recipient"
                      className="block text-sm font-medium text-gray-dark mb-1"
                    >
                      {t("salesDonations.addModal.field.recipient")}
                    </label>
                    <input
                      type="text"
                      id="recipient"
                      name="recipient"
                      value={newTransaction.recipient}
                      onChange={handleInputChange}
                      required
                      className="w-full border border-gray-medium p-2"
                    />
                  </div>
                )}

                <div>
                  <label
                    htmlFor="taxYear"
                    className="block text-sm font-medium text-gray-dark mb-1"
                  >
                    {t("salesDonations.addModal.field.taxYear")}
                  </label>
                  <input
                    type="number"
                    id="taxYear"
                    name="taxYear"
                    value={newTransaction.taxYear}
                    onChange={handleInputChange}
                    required
                    min="2000"
                    max="2100"
                    className="w-full border border-gray-medium p-2"
                  />
                </div>

                <div>
                  <label
                    htmlFor="details"
                    className="block text-sm font-medium text-gray-dark mb-1"
                  >
                    {t("salesDonations.addModal.field.details")}
                  </label>
                  <textarea
                    id="details"
                    name="details"
                    rows="3"
                    value={newTransaction.details}
                    onChange={handleInputChange}
                    className="w-full border border-gray-medium p-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-dark mb-1">
                    {t("salesDonations.addModal.field.documents")}
                  </label>
                  <div className="border border-dashed border-gray-medium p-4 text-center">
                    <button
                      type="button"
                      className="btn-artsy btn-secondary-artsy text-sm"
                      onClick={() => setShowUploadModal(true)}
                    >
                      {t("salesDonations.addModal.addDocuments")}
                    </button>

                    <p className="text-xs text-gray-dark mt-2">
                      {t("salesDonations.addModal.documentsHelper")}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="btn-artsy btn-secondary-artsy mr-2"
                >
                  {t("common.cancel")}
                </button>
                <button
                  type="submit"
                  className="btn-artsy btn-primary-artsy"
                >
                  {t("common.save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal détails */}
      {showDetailsModal && selectedTransaction && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
          onClick={() => setShowDetailsModal(false)}
          onKeyDown={(e) =>
            e.key === "Escape" && setShowDetailsModal(false)
          }
          tabIndex={-1}
        >
          <div
            className="bg-white p-8 rounded-md max-w-lg w-full max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-6">
              <h3 className="font-serif text-xl">
                {t("salesDonations.details.title")}
              </h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-500 hover:text-black"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex justify-between border-b pb-3">
                <span className="font-medium">
                  {t("salesDonations.details.field.type")}
                </span>
                <span>{transactionTypeLabel(selectedTransaction.type)}</span>
              </div>

              <div className="flex justify-between border-b pb-3">
                <span className="font-medium">
                  {t("salesDonations.details.field.artwork")}
                </span>
                <span>{selectedTransaction.artwork}</span>
              </div>

              <div className="flex justify-between border-b pb-3">
                <span className="font-medium">
                  {t("salesDonations.details.field.artist")}
                </span>
                <span>{selectedTransaction.artist}</span>
              </div>

              <div className="flex justify-between border-b pb-3">
                <span className="font-medium">
                  {t("salesDonations.details.field.date")}
                </span>
                <span>{formatDateValue(selectedTransaction.date)}</span>
              </div>

              <div className="flex justify-between border-b pb-3">
                <span className="font-medium">
                  {selectedTransaction.type === "Vente"
                    ? t("salesDonations.details.field.amount")
                    : t("salesDonations.details.field.estimatedValue")}
                </span>
                <span>
                  {formatMoney(
                    selectedTransaction.type === "Vente"
                      ? selectedTransaction.amount || 0
                      : selectedTransaction.valuationAmount || 0
                  )}
                </span>
              </div>

              <div className="flex justify-between border-b pb-3">
                <span className="font-medium">
                  {selectedTransaction.type === "Vente"
                    ? t("salesDonations.details.field.buyer")
                    : t("salesDonations.details.field.recipient")}
                </span>
                <span>
                  {selectedTransaction.type === "Vente"
                    ? selectedTransaction.buyer
                    : selectedTransaction.recipient}
                </span>
              </div>

              <div className="flex justify-between border-b pb-3">
                <span className="font-medium">
                  {t("salesDonations.details.field.taxYear")}
                </span>
                <span>{selectedTransaction.taxYear}</span>
              </div>

              <div className="border-b pb-3">
                <p className="font-medium mb-1">
                  {t("salesDonations.details.field.details")}
                </p>
                <p className="text-sm">
                  {selectedTransaction.details || "—"}
                </p>
              </div>

              <div>
                <p className="font-medium mb-2">
                  {t("salesDonations.details.field.documents")}
                </p>
                <div className="space-y-2">
                  {(selectedTransaction.documents || []).map(
                    (doc, index) => (
                      <div
                        key={index}
                        className="flex justify-between items-center p-2 border border-gray-medium"
                      >
                        <span>{doc}</span>
                        <button className="text-sm underline">
                          {t("salesDonations.details.viewDocument")}
                        </button>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>

            <div className="mt-8 flex justify-between">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="btn-artsy btn-secondary-artsy"
              >
                {t("common.close")}
              </button>
              <button
                className="btn-artsy btn-primary-artsy"
                onClick={() =>
                  handleExportSingleYear(selectedTransaction.taxYear)
                }
              >
                {t("salesDonations.summary.exportYear")}
              </button>
            </div>
          </div>
        </div>
      )}

      <UploadDocumentsModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
      />
    </div>
  );
}